import { Component, OnInit, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterLink } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';

import { HeaderComponent } from '../../common/header/header.component';
import { SidebarComponent } from '../../common/slidebar/sidebar.components';
import { ContactFormDialogComponent } from '../contact-form-dialog/contact-form-dialog.component';
import { ContactDirectoryService } from '../../../services/contact-directory/contact-directory.service';

@Component({
  selector: 'app-leaderboard',
  imports: [HeaderComponent, RouterLink, CommonModule, FormsModule, SidebarComponent],
  templateUrl: './leaderboard.component.html',
  styleUrl: './leaderboard.component.css',
})
export class LeaderboardComponent implements OnInit {
  leaderboardData: any[] = [];
  top3Entries: any[] = [];
  userRank: number | null = null;
  userCredits: number = 0;
  userApprovedSubmissions: number = 0;
  isLoading: boolean = false;
  selectedEntry: any | null = null;

  private readonly medals = ['🥇', '🥈', '🥉'];

  constructor(
    private dialog: MatDialog,
    private contactDirectoryService: ContactDirectoryService,
    private cdr: ChangeDetectorRef,
  ) { }

  onContributorImageError(entry: any) {
    entry.imageUrl = null;
    this.cdr.detectChanges();
  }

  ngOnInit() {
    this.loadLeaderboardData();
  }

  loadLeaderboardData() {
    this.isLoading = true;
    this.contactDirectoryService.getLeaderboard().subscribe({
      next: (response: any) => {
        if (response && response.data) {
          this.leaderboardData = this.mapLeaderboardData(response.data).sort(
            (a, b) => (a.rank ?? 0) - (b.rank ?? 0),
          );
          this.top3Entries = this.leaderboardData
            .slice()
            .sort((a, b) => (a.rank ?? 0) - (b.rank ?? 0))
            .filter((e) => e.rank >= 1 && e.rank <= 3);
          this.updateUserStats(response.data);
        } else {
          this.leaderboardData = [];
          this.top3Entries = [];
        }
        this.isLoading = false;
      },
      error: (error: any) => {
        console.error('Error loading leaderboard:', error);
        this.leaderboardData = [];
        this.top3Entries = [];
        this.isLoading = false;
      },
    });
  }

  mapLeaderboardData(data: any): any[] {
    if (data.leaderboard && Array.isArray(data.leaderboard)) {
      const myRank = data.my_rank || null;
      return data.leaderboard.map((item: any) => ({
        rank: item.rank || 0,
        displayName: item.name || 'N/A',
        contributor: item.name || 'N/A',
        imageUrl: item.image_url || null,
        email: item.email || 'N/A',
        // Client design expects: credits, contributions, approved.
        // API field names vary, so keep fallbacks to avoid breaking.
        credits:
          parseFloat(item.credits ?? item.total_credits ?? item.totalCredits ?? 0) ||
          0,
        contributions:
          parseInt(
            item.contributions_count ??
              item.contribution_count ??
              item.contributions ??
              item.submission_count ??
              0,
            10,
          ) || 0,
        approved:
          parseInt(
            item.approved_count ??
              item.approved_submissions ??
              item.approved ??
              item.submission_count ??
              0,
            10,
          ) || 0,
        userId: item.user_id || null,
        approvedSubmissions:
          parseInt(item.submission_count) ||
          parseInt(item.approved_count ?? 0) ||
          0,
        totalCredits:
          parseFloat(item.total_credits) ||
          parseFloat(item.credits ?? 0) ||
          0,
        isCurrentUser: myRank !== null && item.rank === myRank,
      }));
    }
    return [];
  }

  updateUserStats(data: any) {
    // Update user stats from API response using the new structure
    if (data.my_rank !== undefined && data.my_rank !== null) {
      this.userRank = parseInt(data.my_rank) || null;
    } else {
      this.userRank = null;
    }
    if (data.my_total_credits !== undefined && data.my_total_credits !== null) {
      this.userCredits = parseFloat(data.my_total_credits) || 0;
    } else {
      this.userCredits = 0;
    }
    if (
      data.my_submission_count !== undefined &&
      data.my_submission_count !== null
    ) {
      this.userApprovedSubmissions = parseInt(data.my_submission_count) || 0;
    } else {
      this.userApprovedSubmissions = 0;
    }
  }

  selectEntry(entry: any) {
    // Toggle selection - if same entry clicked, deselect it
    if (this.selectedEntry && this.selectedEntry.rank === entry.rank) {
      this.selectedEntry = null;
    } else {
      this.selectedEntry = entry;
    }
  }

  getInitials(value: any): string {
    const name = (value ?? '').toString().trim();
    if (!name) return '';
    const parts = name.split(/\s+/).filter(Boolean);
    if (parts.length === 1) return (parts[0].slice(0, 2)).toUpperCase();
    return `${(parts[0][0] ?? '').toUpperCase()}${(parts[1][0] ?? '').toUpperCase()}`;
  }

  getEntryMedal(rank: number): string {
    if (rank === 1) return this.medals[0];
    if (rank === 2) return this.medals[1];
    if (rank === 3) return this.medals[2];
    return `#${rank}`;
  }

  getTop3Entries(): any[] {
    return this.leaderboardData
      .slice()
      .sort((a, b) => (a.rank ?? 0) - (b.rank ?? 0))
      .filter((e) => e.rank >= 1 && e.rank <= 3);
  }

  onMakeContribution() {
    const dialogRef = this.dialog.open(ContactFormDialogComponent, {
      width: '520px',
      maxWidth: '95vw',
      disableClose: false,
      panelClass: 'contact-form-dialog',
      autoFocus: false,
      hasBackdrop: true,
      backdropClass: 'contact-form-dialog-backdrop',
      enterAnimationDuration: '300ms',
      exitAnimationDuration: '300ms',
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result && result.success) {
        this.loadLeaderboardData();
      }
    });
  }
}
